// Saving ONE shared-trip photo to disk.
//
// WHY THIS ISN'T `<a href={photo.url} download>`. The `download` attribute is
// ignored for a cross-origin href, and a presigned photo URL lives at the S3 REST
// endpoint -- a different origin from the site. Such an anchor navigates and
// displays the photo inline; it never saves it. So the real choice was never
// "anchor vs blob", it was "blob vs a SECOND presigned URL carrying
// ResponseContentDisposition: attachment".
//
// WHY NOT THAT SECOND URL. amplify/functions/shared/entryPhotos.mjs caps the
// shared payload at MAX_SHARED_PHOTOS for one stated reason: "a Function URL does
// not compress its response and a presigned URL is ~700 bytes. Don't 'optimise'
// it away." A download URL per photo is another ~170 KB of uncompressed response
// on every page load, doubling the exact cost that constant exists to bound --
// to buy something the browser does for free. The attachments bucket already
// answers a cross-origin fetch of a presigned GET (Amplify's default CORS is
// `allowedOrigins: ['*']` with `allowedHeaders: ['*']`), and photoZip.mjs needs
// that same capability anyway, so both features now rest on one assumption
// instead of two: if that CORS ever narrows they fail together and visibly.
//
// THE DOWNLOAD ASKS FOR THE NETWORK, and it has to. This used to lean on the
// browser's HTTP cache serving the bytes the thumbnail had already pulled --
// same URL, and S3 sends ETag/Last-Modified. That is exactly what broke it. The
// thumbnail is a plain <img>, so its request carries no Origin, and S3 answers
// one of those with neither Access-Control-Allow-Origin NOR Vary. Nothing in
// the stored response says it may not be handed to a CORS request, so the
// download's fetch was served that cached copy, failed the CORS check against
// it, and rejected with a bare "Failed to fetch" before any status existed to
// report. `cache: 'reload'` is the fix: go to S3, which answers an Origin-
// bearing request with the header, and the visitor gets their photo.
//
// THE ORIGINAL, NEVER THE PREVIEW. `photo.preview` is a 512 px JPEG that exists
// so a strip of thumbnails isn't tens of megabytes. Downloading it would hand
// someone a postage stamp of their own holiday.

import { downloadBlob } from './downloadFile.mjs';
import { displayPhotoName } from '../fleet-shared/Attachments.mjs';

// What the saved file should be called. A visitor's upload is stored under a
// generated key (`guest-<stamp>-<token>-holiday.jpg`) so it can't collide with
// the owner's files, and none of that bookkeeping belongs in a filename.
export function downloadNameFor(photo) {
  return displayPhotoName(photo?.name || 'photo.jpg');
}

// A failed fetch of a presigned URL is overwhelmingly one thing: the URL aged
// out. They are minted per page load with a one-hour ceiling, and a page that
// has been open longer than that -- or was bookmarked -- has nothing but dead
// links on it. Saying "reload the page" is actionable; "download failed" is not.
export function expiredPhotoError(status) {
  const err = new Error(
    'That photo link has expired. Reload the page to get fresh links and try again.'
  );
  err.name = 'PhotoLinkExpiredError';
  err.status = status ?? null;
  return err;
}

// Fetch one photo's ORIGINAL and hand it to the browser as a save.
//
// Rejects rather than reporting: the caller decides where a failure is shown,
// and on this page that is inline in the viewer, never the page's status banner
// (one unreadable photo must not look like a page that failed to load).
export async function downloadPhoto(photo, { signal, fetchImpl = fetch } = {}) {
  if (!photo?.url) {
    throw new Error('That photo has no link to download.');
  }

  const response = await fetchImpl(photo.url, { signal, cache: 'reload' });
  if (!response.ok) {
    // 403 is what S3 answers for an expired signature; treat the whole
    // client-error range that way rather than guessing at the body.
    if (response.status === 403 || response.status === 404) {
      throw expiredPhotoError(response.status);
    }
    throw new Error(`Could not download that photo (${response.status}).`);
  }

  const blob = await response.blob();
  downloadBlob(downloadNameFor(photo), blob);
  return blob.size;
}
